/**
 * Mappings d'affichage centralisés pour le domaine Orders.
 * Aligné sur les enums API : api/src/modules/orders/entities/enums.ts
 *
 * Aucune logique métier ici — juste des Records `code → libellé français`.
 */

import type {
	CustomerLite,
	CustomerType,
	DeliveryMode,
	OrderEventType,
	OrderSource,
	OrderStatus,
	PaymentMethod,
	PaymentStatus,
	PickupStatus,
	RefundStatus,
	ReturnDecision,
	ReturnLineCondition,
	ReturnReasonCode,
	ReturnStatus,
} from "./types";

/**
 * Sous-libellé d'étape — humanisation du statut courant pour la colonne « Étape »
 * de la liste et le sous-titre du détail.
 */
export const ORDER_STATUS_SUB_LABEL: Record<OrderStatus, string> = {
	DRAFT: "Brouillon",
	PENDING_PAYMENT: "Attente paiement",
	PAID: "Confirmation requise",
	CONFIRMED: "À préparer",
	PROCURING: "Approvisionnement",
	IN_HUB: "Au hub",
	READY_TO_SHIP: "Prête expédition",
	SHIPPING: "Tournée en cours",
	DELIVERED: "Livrée",
	COMPLETED: "Clôturée",
	CANCELLED: "Annulée",
	REFUNDED: "Remboursée",
	DISPUTED: "Litige en cours",
};

// -- Statuts de commande (13) --
export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
	DRAFT: "Brouillon",
	PENDING_PAYMENT: "À payer",
	PAID: "Payée",
	CONFIRMED: "Confirmée",
	PROCURING: "Approvisionnement",
	IN_HUB: "Au hub",
	READY_TO_SHIP: "Prête à expédier",
	SHIPPING: "En livraison",
	DELIVERED: "Livrée",
	COMPLETED: "Clôturée",
	CANCELLED: "Annulée",
	REFUNDED: "Remboursée",
	DISPUTED: "Litige",
};

// -- Mode de paiement (5) --
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
	MOMO: "MoMo MTN",
	OM: "Orange Money",
	BANK_TRANSFER: "Virement bancaire",
	COD: "Espèces (COD)",
	CARD: "Carte bancaire",
};

// -- Statut de paiement (5) --
export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
	UNPAID: "Non payée",
	AUTHORIZED: "Autorisée",
	CAPTURED: "Capturée",
	REFUNDED: "Remboursée",
	FAILED: "Échec",
};

// -- Mode de livraison (3) --
export const DELIVERY_MODE_LABEL: Record<DeliveryMode, string> = {
	STANDARD: "Livraison standard",
	EXPRESS: "Express 24 h",
	HUB_PICKUP: "Retrait au hub",
};

// -- Source de commande (4) --
export const ORDER_SOURCE_LABEL: Record<OrderSource, string> = {
	WEB: "Site web",
	APP: "Application mobile",
	ADMIN_MANUAL: "Saisie manuelle (admin)",
	PARTNER: "Partenaire / API",
};

// -- Type de compte client (2) --
export const CUSTOMER_TYPE_LABEL: Record<CustomerType, string> = {
	b2c: "Particulier",
	b2b: "Entreprise",
};

/**
 * À qui l'on a affaire, en un mot.
 *
 * « Invité » prime sur le type : une commande passée sans compte se traite
 * autrement (pas d'historique connu, coordonnées à vérifier au téléphone), et
 * c'est l'information la plus utile à voir d'abord.
 */
export function customerKindLabel(
	customer: CustomerLite | null | undefined,
): string {
	if (!customer) return "—";
	if (customer.status === "guest") return "Invité (sans compte)";
	return CUSTOMER_TYPE_LABEL[customer.type ?? "b2c"];
}

// -- Statut pickup de ligne (4) --
export const PICKUP_STATUS_LABEL: Record<PickupStatus, string> = {
	NOT_STARTED: "Pas commencé",
	SCHEDULED: "Planifié",
	PICKED_UP: "Retiré chez fournisseur",
	IN_HUB: "Au hub",
};

// -- Type d'événement de commande (humanisation des types techniques) --
/**
 * Liste alignée sur api/src/modules/orders/entities/enums.ts ORDER_EVENT_TYPES.
 * Quelques alias ajoutés pour rétrocompat (anciens projets internes).
 */
export const ORDER_EVENT_TYPE_LABEL: Record<string, string> = {
	STATUS_CHANGED: "Changement de statut",
	PAYMENT_RECEIVED: "Paiement reçu",
	PAYMENT_FAILED: "Paiement échoué",
	LINE_ADDED: "Ligne ajoutée",
	LINE_REMOVED: "Ligne supprimée",
	LINE_UPDATED: "Ligne mise à jour",
	PRICE_OVERRIDE: "Prix surchargé",
	ADDRESS_UPDATED: "Adresse mise à jour",
	NOTE_ADDED: "Note ajoutée",
	PROMO_APPLIED: "Promotion appliquée",
	CANCELLED: "Commande annulée",
	REFUND_ISSUED: "Remboursement émis",
	RETURN_REQUESTED: "Retour demandé",
	PICKUP_SCHEDULED: "Approvisionnement planifié",
	PICKUP_COMPLETED: "Approvisionnement terminé",
	INVOICE_EMITTED: "Facture émise",
	FLAG_CHANGED: "Indicateur modifié",
	// Prévu au contrat, pas encore émis par l'API : le journal le rendra dès le
	// premier événement reçu, sans nouvelle livraison de l'admin.
	NOTIFICATION_SENT: "Notification envoyée",
	// Alias historiques (compat)
	HUB_ASSIGNED: "Hub assigné",
	OPERATOR_ASSIGNED: "Opérateur assigné",
	REFUND_REQUESTED: "Remboursement demandé",
	REFUND_EXECUTED: "Remboursement exécuté",
	RETURN_ACCEPTED: "Retour accepté",
	RETURN_REFUSED: "Retour refusé",
	RETURN_RECEIVED: "Retour reçu au hub",
	RETURN_INSPECTED: "Retour inspecté",
	FRAUD_FLAGGED: "Suspicion de fraude",
	FRAUD_CLEARED: "Fraude écartée",
	STOCK_RESERVED: "Stock réservé",
	STOCK_RELEASED: "Stock libéré",
	INVOICE_GENERATED: "Facture générée",
	WAYBILL_GENERATED: "Bordereau généré",
	WEBHOOK_RECEIVED: "Webhook reçu",
	CUSTOMER_NOTIFIED: "Client notifié",
};

/**
 * Catégorie d'un événement → couleur (sert pour la pastille/barre timeline).
 * Utilise les variables CSS du thème.
 */
export type EventColor =
	| "success"
	| "warning"
	| "danger"
	| "info"
	| "neutral"
	| "terra";

export function orderEventColor(type: string): EventColor {
	if (type === "PAYMENT_RECEIVED" || type === "INVOICE_EMITTED")
		return "success";
	if (
		type === "PAYMENT_FAILED" ||
		type === "CANCELLED" ||
		type === "FRAUD_FLAGGED"
	)
		return "danger";
	if (
		type === "REFUND_ISSUED" ||
		type === "REFUND_REQUESTED" ||
		type === "REFUND_EXECUTED" ||
		type === "RETURN_REQUESTED" ||
		type === "RETURN_ACCEPTED" ||
		type === "RETURN_REFUSED" ||
		type === "RETURN_RECEIVED" ||
		type === "RETURN_INSPECTED"
	)
		return "warning";
	if (
		type === "STATUS_CHANGED" ||
		type === "NOTIFICATION_SENT" ||
		type === "PICKUP_SCHEDULED" ||
		type === "PICKUP_COMPLETED" ||
		type === "HUB_ASSIGNED" ||
		type === "OPERATOR_ASSIGNED"
	)
		return "info";
	if (type === "PRICE_OVERRIDE" || type === "PROMO_APPLIED") return "terra";
	if (
		type === "NOTE_ADDED" ||
		type === "ADDRESS_UPDATED" ||
		type === "FLAG_CHANGED"
	)
		return "neutral";
	return "neutral";
}

// -- Notifications client (événement NOTIFICATION_SENT du journal) --
export const NOTIFICATION_CHANNEL_LABEL: Record<string, string> = {
	sms: "SMS",
	whatsapp: "WhatsApp",
	email: "E-mail",
	push: "Notification push",
};

export const NOTIFICATION_RESULT_LABEL: Record<string, string> = {
	pending: "En cours d'envoi",
	sent: "Envoyée",
	delivered: "Reçue",
	failed: "Échec",
	skipped_optout: "Non envoyée (désinscription)",
};

export interface NotificationDetails {
	channel: string | null;
	template: string | null;
	recipient: string | null;
	result: string | null;
}

/**
 * Détail d'un envoi tel que le journal le porte — ou ne le porte pas.
 *
 * Chaque champ absent reste `null` au lieu d'être comblé par une valeur
 * plausible : sur l'écran qui sert à savoir si le client a bien été prévenu,
 * une valeur inventée coûte plus cher qu'un tiret. Les deux clés acceptées par
 * champ correspondent aux deux nommages en circulation — celui du journal de
 * messagerie (`event`, `status`) et celui du contrat d'événement de commande.
 */
export function readNotificationDetails(
	payload: Record<string, unknown> | null | undefined,
): NotificationDetails {
	const texte = (v: unknown): string | null =>
		typeof v === "string" && v.trim() !== "" ? v.trim() : null;
	const p = payload ?? {};
	return {
		channel: texte(p.channel),
		template: texte(p.template) ?? texte(p.event),
		recipient: texte(p.recipient),
		result: texte(p.result) ?? texte(p.status),
	};
}

/** Vrai si l'événement porte au moins une information exploitable. */
export function hasNotificationDetails(d: NotificationDetails): boolean {
	return Boolean(d.channel || d.template || d.recipient || d.result);
}

/**
 * Couleur CSS (var) correspondante — pour la barre verticale / pastille.
 */
export const EVENT_COLOR_CSS: Record<EventColor, string> = {
	success: "var(--a-success, #1f9d55)",
	warning: "var(--a-warning, #c98d10)",
	danger: "var(--a-danger, #b23a2a)",
	info: "var(--a-info, #2563b6)",
	terra: "var(--a-terracotta, #c6563a)",
	neutral: "var(--a-border-strong, #c9bfae)",
};

// -- Motifs d'annulation / changement de statut (libres mais souvent code-like) --
export const CANCELLATION_REASON_LABEL: Record<string, string> = {
	CUSTOMER_REQUEST: "Demande client",
	PAYMENT_FAILED: "Échec de paiement",
	PAYMENT_EXPIRED: "Délai de paiement dépassé",
	OUT_OF_STOCK: "Rupture de stock",
	FRAUD_SUSPECTED: "Suspicion de fraude",
	DELIVERY_REFUSED: "Livraison refusée par le client",
	WRONG_ADDRESS: "Adresse incorrecte",
	CHANGE_MIND: "Changement d'avis",
	CHANGE_MIND_QA: "Changement d'avis (QA)",
	PRICE_ERROR: "Erreur de prix",
	DUPLICATE: "Commande en doublon",
	FLOW_TEST: "Test workflow (QA)",
	MANUAL_TEST: "Test manuel (QA)",
	OTHER: "Autre motif",
};

// -- Statuts de retour (8) --
export const RETURN_STATUS_LABEL: Record<ReturnStatus, string> = {
	REQUESTED: "Demande",
	ACCEPTED: "Acceptée",
	REJECTED: "Refusée",
	IN_TRANSIT: "En transit",
	RECEIVED: "Reçue au hub",
	INSPECTED: "Inspectée",
	REFUNDED: "Remboursée",
	DISPUTED: "Litige ouvert",
};

// -- Motifs de retour (7) --
export const RETURN_REASON_LABEL: Record<ReturnReasonCode, string> = {
	WRONG_SIZE: "Mauvaise taille",
	DEFECT: "Produit défectueux",
	NOT_AS_DESCRIBED: "Non conforme à la description",
	CHANGE_OF_MIND: "Changement d'avis",
	DAMAGED_DELIVERY: "Endommagé pendant la livraison",
	WRONG_ITEM: "Mauvais article reçu",
	OTHER: "Autre motif",
};

// -- Décisions de retour (3) --
export const RETURN_DECISION_LABEL: Record<ReturnDecision, string> = {
	FULL_REFUND: "Remboursement total",
	PARTIAL_REFUND: "Remboursement partiel",
	REFUSED: "Refusé (article non éligible)",
};

// -- Condition de ligne retournée (4) --
export const RETURN_CONDITION_LABEL: Record<ReturnLineCondition, string> = {
	NEW: "Neuf scellé",
	USED: "Usagé",
	DAMAGED: "Endommagé",
	MISSING_PARTS: "Pièces manquantes",
};

// -- Statut de remboursement (3) --
export const REFUND_STATUS_LABEL: Record<RefundStatus, string> = {
	PENDING: "En attente",
	EXECUTED: "Exécuté",
	FAILED: "Échec",
};

// -- Flags commande --
export const ORDER_FLAG_LABEL: Record<string, string> = {
	vip: "Client VIP",
	first_order: "Première commande",
	fraud_suspected: "Suspicion de fraude",
	urgent: "Urgent",
	heavy: "Volumineux",
	grouped_delivery: "Livraison groupée",
};

/** Helper générique : récupère un label ou retourne le code tel quel. */
export function safeLabel<T extends string>(
	map: Record<string, string>,
	code: T | null | undefined,
): string {
	if (!code) return "—";
	return map[code] ?? code;
}

/** Pluriel français simple : 0/1 → singulier, ≥2 → pluriel. */
export function plural(
	n: number,
	singular: string,
	pluralForm?: string,
): string {
	if (n <= 1) return `${n} ${singular}`;
	return `${n} ${pluralForm ?? singular + "s"}`;
}

// -- Réexport ORDER_EVENT_TYPE_LABEL avec helper typed --
export function orderEventLabel(type: OrderEventType | string): string {
	return safeLabel(ORDER_EVENT_TYPE_LABEL, type);
}

/**
 * Résout un `reasonCode` peu importe son origine (annulation, retour, libre).
 * Ordre de lookup : cancellation → return → return decision → libre humanisé.
 */
export function resolveReasonLabel(code: string | null | undefined): string {
	if (!code) return "—";
	if (CANCELLATION_REASON_LABEL[code]) return CANCELLATION_REASON_LABEL[code];
	if (RETURN_REASON_LABEL[code as ReturnReasonCode])
		return RETURN_REASON_LABEL[code as ReturnReasonCode];
	if (RETURN_DECISION_LABEL[code as ReturnDecision])
		return RETURN_DECISION_LABEL[code as ReturnDecision];
	// Heuristique : code SNAKE_UPPER → humanisé en première lettre majuscule
	if (/^[A-Z][A-Z0-9_]+$/.test(code)) {
		return code
			.toLowerCase()
			.replace(/_/g, " ")
			.replace(/^./, (c) => c.toUpperCase());
	}
	return code;
}
